// src/app/core/auth/auth.service.ts
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { env } from 'src/environments/environment';
import { AuthUser, LoginResponse, OAuthResponse, UserStatus } from './models/auth-user.model';
import { LoginRequest, RegisterRequest } from './models/auth-request.model';
import { OutlineService } from './outline.service';

export type { AuthUser as UserAuthData };

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private readonly apiUrl = env.apiUrl;
  private readonly TOKEN_KEY = 'auth_token';
  private readonly USER_KEY = 'auth_user';
  private readonly RECOVERY_SESSION_KEY = 'recovery_session';

  private currentUserSubject = new BehaviorSubject<AuthUser | null>(this.getUserFromStorage());
  public currentUser$ = this.currentUserSubject.asObservable();

  private recoverySessionSubject = new BehaviorSubject<boolean>(
    localStorage.getItem('recovery_session') === 'true'
  );
  public isRecoverySession$ = this.recoverySessionSubject.asObservable();

  constructor(
    private http: HttpClient,
    private router: Router,
    private outlineService: OutlineService
  ) {}

  // ─────────────────────────────────────────────
  // LOGIN
  // ─────────────────────────────────────────────

  login(data: LoginRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.apiUrl}/auth/login`, data).pipe(
      tap(response => this.saveSession(response))
    );
  }

  // ─────────────────────────────────────────────
  // REGISTRO
  // ─────────────────────────────────────────────

  register(data: RegisterRequest): Observable<any> {
    return this.http.post(`${this.apiUrl}/auth/register`, data);
  }

  // ─────────────────────────────────────────────
  // SESIÓN
  // ─────────────────────────────────────────────

  logout(): void {
    this.http.post(`${this.apiUrl}/auth/logout`, {}).subscribe({ error: () => {} });
    this.outlineService.invalidarCache();
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem(this.RECOVERY_SESSION_KEY);
    this.currentUserSubject.next(null);
    this.recoverySessionSubject.next(false);
    // Señal para que LoginComponent limpie el caché de MSAL al inicializarse
    sessionStorage.setItem('msal_logout_in_progress', 'true');
    this.router.navigate(['/login'], { queryParams: { logout: 'true' } });
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;
    if (this.isTokenExpired(token)) {
      this.logout();
      return false;
    }
    return true;
  }

  getToken(): string | null {
    const token = localStorage.getItem(this.TOKEN_KEY);
    if (!token || token === 'undefined' || token === 'null') return null;
    return token;
  }

  getCurrentUser(): AuthUser | null {
    return this.currentUserSubject.value;
  }

  getDisplayName(): string {
    const user = this.getCurrentUser();
    if (!user) return 'Invitado';
    if (user.nombre) {
      return `${user.nombre} ${user.apellido || ''}`.trim();
    }
    return user.email;
  }

  getUserStatusName(): string {
    const user = this.getCurrentUser();
    if (!user) return '';
    const statusMap: Record<number, string> = {
      [UserStatus.ACTIVO]: 'Activo',
      [UserStatus.INACTIVO]: 'Inactivo',
      [UserStatus.BLOQUEADO]: 'Bloqueado'
    };
    return statusMap[user.status_id] ?? 'Desconocido';
  }

  // ─────────────────────────────────────────────
  // MÉTODOS PÚBLICOS DE SESIÓN
  // ─────────────────────────────────────────────

  saveSession(response: LoginResponse): void {
    localStorage.setItem(this.TOKEN_KEY, response.access_token);
    localStorage.setItem(this.USER_KEY, JSON.stringify(response.data));
    this.currentUserSubject.next(response.data);
  }

  setRecoverySession(): void {
    localStorage.setItem(this.RECOVERY_SESSION_KEY, 'true');
    this.recoverySessionSubject.next(true);
  }

  clearRecoverySession(): void {
    localStorage.removeItem(this.RECOVERY_SESSION_KEY);
    this.recoverySessionSubject.next(false);
  }

  saveOAuthSession(response: OAuthResponse): void {
    localStorage.setItem(this.TOKEN_KEY, response.access_token);
    localStorage.setItem(this.USER_KEY, JSON.stringify(response.data));
    this.currentUserSubject.next(response.data);
  }


  // ─────────────────────────────────────────────
  // MÉTODOS PRIVADOS
  // ─────────────────────────────────────────────

  private getUserFromStorage(): AuthUser | null {
    const userJson = localStorage.getItem(this.USER_KEY);
    if (!userJson || userJson === 'undefined' || userJson === 'null') return null;
    try {
      return JSON.parse(userJson);
    } catch {
      localStorage.removeItem(this.USER_KEY);
      return null;
    }
  }

  private isTokenExpired(token: string): boolean {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return true;

      let payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      while (payload.length % 4) payload += '=';

      const decoded = JSON.parse(atob(payload));
      if (!decoded.exp) return false;

      return decoded.exp < Math.floor(Date.now() / 1000);
    } catch {
      return true;
    }
  }

    // ─────────────────────────────────────────────
    // MÉTODOS DE ACTUALIZACIÓN DE USUARIO
    // ─────────────────────────────────────────────
  updateUser(partialUser: Partial<AuthUser>): void {
    const currentUser = this.getCurrentUser();
    if (!currentUser) return;

    const mergedUser = { ...currentUser, ...partialUser };
    localStorage.setItem(this.USER_KEY, JSON.stringify(mergedUser));
    this.currentUserSubject.next(mergedUser);
  }

  updateUserImage(imageUrl: string): void {
    this.updateUser({ imagen: imageUrl });
  }

}
